import {CardType, ExecutionStatus, NormalizationType, TaskType} from 'constants/dataEnum';
import _ from 'lodash';
import {find} from 'lib/objectHelper';
import PropTypes from 'prop-types';
import {getTasks} from 'stores/selectors/cardStateSelector';
import {useSelector} from 'react-redux';
import {getRuntimeStatus} from 'stores/selectors/runtimeSelector';
import {getLabwareState} from 'stores/selectors/labwareStateSelectors';
import {getNormalizationTaskEditStep} from 'stores/selectors/protocolEditorSelectors';
import NormalizationSetup from './Tasks/NormalizationSetup';
import NormalizationPlateSelectionTask from './Tasks/NormalizationPlateSelectionTask';

function NormalizationTaskContainer({
  card,
  editNormalizationTaskHandler,
  onGetCardExecutionStatus,
  runtimeMode,
  userManagemenBusinessIsUserOperator,
  onSelectTask,
  highlightedTask,
  onArrowDownPress,
  onArrowUpPress,
  readOnly,
}) {
  const protocolNormalizationTaskEditStep = useSelector(getNormalizationTaskEditStep);
  const runtimeStateStatus = useSelector(getRuntimeStatus);
  const cardStateTasks = useSelector(getTasks);
  const labwareState = useSelector(getLabwareState);
  const {
    normalizationState: {type: normalizationType},
  } = card;
  const isFixedDiluent = normalizationType === NormalizationType.FIXED_DILUENT_VOLUME;

  const isEdit = !!protocolNormalizationTaskEditStep;

  const tasks = Object.values(_.orderBy(_.pick(cardStateTasks, card.tasks), ['number'], ['asc']));

  const taskAspirate = tasks.find((e) => e.type === TaskType.ASPIRATE);
  const taskDispense = tasks.find((e) => e.type === TaskType.DISPENSE);

  const samplePlate = find(labwareState, {
    LabwareID: !isFixedDiluent ? taskDispense?.labwareID : taskAspirate?.labwareID,
  })?.name;

  const diluentSource = find(labwareState, {
    LabwareID: !isFixedDiluent ? taskAspirate?.labwareID : taskDispense?.labwareID,
  })?.name;

  const onEditNormalizationTaskHandler = (number) => {
    onSelectTask(null, {bypassValidityChecking: true});
    editNormalizationTaskHandler(number);
  };

  return (
    <>
      <NormalizationPlateSelectionTask
        key="normalization-1"
        number={1}
        plate={samplePlate}
        runtimeMode={runtimeMode}
        executionStatus={onGetCardExecutionStatus(card.ID)}
        onEdit={onEditNormalizationTaskHandler}
        showEditButton={!userManagemenBusinessIsUserOperator() && !isEdit && !readOnly}
        onSelectTask={() =>
          onSelectTask(1, CardType.NORMALIZATION, [isFixedDiluent ? taskAspirate : taskDispense], card.ID)
        }
        isHighlighted={
          highlightedTask?.type === CardType.NORMALIZATION &&
          highlightedTask?.cardID === card.ID &&
          highlightedTask?.ID === 1
        }
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
      />
      <NormalizationPlateSelectionTask
        key="normalization-2"
        number={2}
        plate={diluentSource}
        runtimeMode={runtimeMode}
        executionStatus={onGetCardExecutionStatus(card.ID)}
        onEdit={onEditNormalizationTaskHandler}
        showEditButton={!userManagemenBusinessIsUserOperator() && !isEdit && !readOnly}
        onSelectTask={() =>
          onSelectTask(2, CardType.NORMALIZATION, [isFixedDiluent ? taskAspirate : taskDispense], card.ID)
        }
        isHighlighted={
          highlightedTask?.type === CardType.NORMALIZATION &&
          highlightedTask?.cardID === card.ID &&
          highlightedTask?.ID === 2
        }
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
      />
      <NormalizationSetup
        key="normalization-3"
        number={3}
        targetConcentration={card.normalizationState.targetConcentration}
        currentVolume={card.normalizationState.currentVolume}
        runtimeMode={runtimeMode}
        executionStatus={onGetCardExecutionStatus(card.ID)}
        onEdit={onEditNormalizationTaskHandler}
        showEditButton={!userManagemenBusinessIsUserOperator() && !isEdit && !readOnly}
        onSelectTask={() =>
          onSelectTask(3, CardType.NORMALIZATION, [isFixedDiluent ? taskAspirate : taskDispense], card.ID)
        }
        normalizationType={normalizationType}
        isHighlighted={
          highlightedTask?.type === CardType.NORMALIZATION &&
          highlightedTask?.cardID === card.ID &&
          highlightedTask?.ID === 3
        }
        onArrowDownPress={onArrowDownPress}
        onArrowUpPress={onArrowUpPress}
        readOnly={
          readOnly ||
          onGetCardExecutionStatus(card.ID) === ExecutionStatus.EXECUTED ||
          userManagemenBusinessIsUserOperator()
        }
        runtimeStatus={runtimeStateStatus}
        card={card}
      />
    </>
  );
}

NormalizationTaskContainer.propTypes = {
  card: PropTypes.object.isRequired,
  editNormalizationTaskHandler: PropTypes.func.isRequired,
  onGetCardExecutionStatus: PropTypes.func.isRequired,
  runtimeMode: PropTypes.bool.isRequired,
  userManagemenBusinessIsUserOperator: PropTypes.func.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  highlightedTask: PropTypes.object.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
};

export default NormalizationTaskContainer;
